import { useAmplitudeAnalytics } from '@cw-elements/analytics-provider';
import { useCallback } from 'react';

import { useApp } from '@/contexts';

import type { EventParams, TrackEventFunction } from './types';

/**
 * Hook for tracking application events. Every event carries the selected
 * region and crop season as context.
 *
 * The analytics identity (user id and attributes) is not this hook's concern —
 * `AnalyticsUserIdentifier` owns it app-wide.
 *
 * @returns A function to track events with optional parameters
 *
 * @example
 * const trackEvent = useTrackEvent();
 * trackEvent('button_clicked');
 * trackEvent('form_submitted', { property1: 'value1', property2: 'value2' });
 */
const useTrackEvents = (): TrackEventFunction => {
  const {
    selectedCropSeason,
    selectedCropSeasonName,
    selectedRegion,
    selectedRegionName
  } = useApp();
  const trackEventInstance = useAmplitudeAnalytics();

  const trackEvent = useCallback(
    (eventName: string, params?: EventParams) => {
      trackEventInstance(eventName, {
        cropSeasonId: selectedCropSeason,
        cropSeasonName: selectedCropSeasonName,
        regionId: selectedRegion,
        regionName: selectedRegionName,
        ...params
      });
    },
    [
      trackEventInstance,
      selectedCropSeason,
      selectedRegion,
      selectedCropSeasonName,
      selectedRegionName
    ]
  );

  return trackEvent;
};

export default useTrackEvents;
